<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { usePolicy } from 'dashboard/composables/usePolicy';
import { useStore } from 'dashboard/composables/store';
import { CAPTAIN_ACCESS_FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import AgentNotice from './experience/components/AgentNotice.vue';
import {
  presentAgentLifecycle,
  readAgentLifecycleStatus,
} from './experience/agentStatus';
import {
  readAssistantReadiness,
  readinessPercentage,
} from './experience/readiness';
import { formatApiDate } from './experience/datePresentation';

const ITEMS_PER_PAGE = 12;
const SEARCH_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { checkPermissions } = usePolicy();

const pageLayoutRef = ref(null);
const searchQuery = ref('');
const currentPage = ref(1);
const loadError = ref('');
let searchTimer;

const canManageAssistant = computed(() => checkPermissions(['administrator']));
const assistants = computed(
  () => store.getters['captainAssistants/getRecords'] || []
);
const listMeta = computed(
  () => store.getters['captainAssistants/getMeta'] || {}
);
const uiFlags = computed(
  () => store.getters['captainAssistants/getUIFlags'] || {}
);
const isFetching = computed(() => Boolean(uiFlags.value.fetchingList));
const totalCount = computed(() => Number(listMeta.value.totalCount) || 0);
const hasSearch = computed(() => Boolean(searchQuery.value.trim()));
const hasNoResults = computed(
  () => !loadError.value && hasSearch.value && assistants.value.length === 0
);
const hasNoAgents = computed(
  () => !loadError.value && !hasSearch.value && assistants.value.length === 0
);
const showPagination = computed(
  () => !loadError.value && totalCount.value > ITEMS_PER_PAGE
);

const fetchAssistants = async () => {
  loadError.value = '';
  try {
    await store.dispatch('captainAssistants/get', {
      page: currentPage.value,
      perPage: ITEMS_PER_PAGE,
      searchKey: searchQuery.value.trim(),
    });
  } catch (error) {
    loadError.value =
      error?.message || t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_LOAD_ERROR');
  }
};

const handlePageChange = page => {
  currentPage.value = page;
  fetchAssistants();
};

const clearSearch = () => {
  searchQuery.value = '';
};

const openCreateAssistant = () => {
  if (canManageAssistant.value) {
    pageLayoutRef.value?.openCreateAssistantDialog();
  }
};

const handleAssistantCreated = assistant => {
  if (!assistant?.id) return;
  router.push({
    name: 'captain_assistants_experience_index',
    params: { accountId: route.params.accountId, assistantId: assistant.id },
    query: { section: 'identity' },
  });
};

const openAssistant = assistant => {
  router.push({
    name: 'captain_assistants_experience_index',
    params: { accountId: route.params.accountId, assistantId: assistant.id },
    query: { section: 'overview' },
  });
};

const openExternalAdapters = () => {
  router.push({
    name: 'agent_bots',
    params: { accountId: route.params.accountId },
  });
};

const openExternalIntegrations = () => {
  router.push({
    name: 'settings_integrations_dashboard_apps',
    params: { accountId: route.params.accountId },
  });
};

const assistantManagement = assistant => assistant.agent_configuration || {};
const assistantReadiness = assistant =>
  readAssistantReadiness(assistantManagement(assistant));
const assistantReadinessPercentage = assistant =>
  readinessPercentage(assistantReadiness(assistant));
const assistantLifecycle = assistant => {
  const status = readAgentLifecycleStatus(
    assistantManagement(assistant),
    assistant.config?.publication_mode
  );
  return presentAgentLifecycle(status, t);
};
const hasUnpublishedChanges = assistant =>
  Boolean(assistantManagement(assistant).lifecycle?.has_unpublished_changes);
watch(searchQuery, () => {
  clearTimeout(searchTimer);
  currentPage.value = 1;
  searchTimer = setTimeout(fetchAssistants, SEARCH_DELAY);
});

onMounted(fetchAssistants);
onBeforeUnmount(() => clearTimeout(searchTimer));
</script>

<template>
  <PageLayout
    ref="pageLayoutRef"
    :header-title="t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_TITLE')"
    :button-label="t('CAPTAIN_EXPERIENCE.AUTO.NOVO_AGENTE')"
    :button-policy="['administrator']"
    :feature-flag="CAPTAIN_ACCESS_FEATURE_FLAGS"
    :is-fetching="isFetching"
    :loading-label="t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_LOADING')"
    :current-page="currentPage"
    :total-count="totalCount"
    :items-per-page="ITEMS_PER_PAGE"
    :show-pagination-footer="showPagination"
    :show-assistant-switcher="false"
    :show-know-more="false"
    @click="openCreateAssistant"
    @assistant-created="handleAssistantCreated"
    @update:current-page="handlePageChange"
  >
    <template #subHeader>
      <p class="max-w-2xl pb-4 text-sm leading-relaxed text-n-slate-11">
        {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_DESCRIPTION') }}
      </p>
    </template>

    <template #search>
      <label class="relative min-w-0 flex-1 sm:w-72 sm:flex-none">
        <span class="sr-only">
          {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_SEARCH_LABEL') }}
        </span>
        <span
          class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-9"
          aria-hidden="true"
        />
        <input
          v-model="searchQuery"
          type="search"
          class="min-h-9 w-full rounded-lg border border-n-weak bg-n-solid-2 py-2 pl-9 pr-3 text-sm text-n-slate-12 outline-none transition placeholder:text-n-slate-9 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
          :placeholder="
            t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_SEARCH_PLACEHOLDER')
          "
        />
      </label>
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <AgentNotice
          v-if="loadError"
          tone="error"
          :title="t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_LOAD_ERROR_TITLE')"
          :description="loadError"
          action-icon="i-lucide-refresh-cw"
          :action-label="t('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
          :is-loading="isFetching"
          @action="fetchAssistants"
        />

        <section
          v-else-if="hasNoResults"
          class="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-n-weak bg-n-solid-2 p-6 text-center"
        >
          <span
            class="i-lucide-search-x size-10 text-n-slate-9"
            aria-hidden="true"
          />
          <h2 class="mt-4 text-lg font-semibold text-n-slate-12">
            {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_NO_RESULTS_TITLE') }}
          </h2>
          <p class="mt-2 max-w-md text-sm leading-relaxed text-n-slate-11">
            {{
              t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_NO_RESULTS_DESCRIPTION')
            }}
          </p>
          <Button
            class="mt-4"
            color="slate"
            variant="faded"
            :label="t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_CLEAR_SEARCH')"
            @click="clearSearch"
          />
        </section>

        <section
          v-else-if="hasNoAgents"
          class="overflow-hidden rounded-2xl border border-n-weak bg-gradient-to-br from-n-brand/10 via-n-solid-2 to-n-solid-2 p-6 sm:p-8"
        >
          <div class="max-w-xl">
            <span
              class="i-lucide-bot size-10 text-n-blue-11"
              aria-hidden="true"
            />
            <h2 class="mt-4 text-xl font-semibold text-n-slate-12">
              {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_EMPTY_TITLE') }}
            </h2>
            <p class="mt-2 text-sm leading-relaxed text-n-slate-11">
              {{
                t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_EMPTY_DESCRIPTION')
              }}
            </p>
            <Button
              v-if="canManageAssistant"
              class="mt-5"
              icon="i-lucide-plus"
              :label="t('CAPTAIN_EXPERIENCE.AUTO.CRIAR_AGENTE')"
              @click="openCreateAssistant"
            />
          </div>
        </section>

        <template v-else>
          <p class="text-sm font-medium text-n-slate-10" aria-live="polite">
            {{
              t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_COUNT', {
                count: totalCount,
              })
            }}
          </p>
          <section
            class="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
            :aria-label="t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_TITLE')"
          >
            <button
              v-for="assistant in assistants"
              :key="assistant.id"
              type="button"
              class="group flex min-h-64 flex-col rounded-2xl border border-n-weak bg-n-solid-2 p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
              @click="openAssistant(assistant)"
            >
              <span class="flex items-start justify-between gap-3">
                <span
                  class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-blue-11"
                >
                  <span class="i-lucide-bot size-5" aria-hidden="true" />
                </span>
                <span
                  class="rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="{
                    'bg-n-teal-9/10 text-n-teal-11':
                      assistantLifecycle(assistant).tone === 'teal',
                    'bg-n-brand/10 text-n-blue-11':
                      assistantLifecycle(assistant).tone === 'blue',
                    'bg-n-alpha-2 text-n-slate-11': !['teal', 'blue'].includes(
                      assistantLifecycle(assistant).tone
                    ),
                  }"
                >
                  {{ assistantLifecycle(assistant).label }}
                </span>
              </span>

              <span class="mt-4 min-w-0">
                <span
                  class="block truncate text-base font-semibold text-n-slate-12"
                >
                  {{ assistant.name }}
                </span>
                <span
                  class="mt-1 line-clamp-2 min-h-10 text-sm leading-relaxed text-n-slate-11"
                >
                  {{
                    assistant.description ||
                    t(
                      'CAPTAIN_EXPERIENCE.SCRIPT.SELECTED_ASSISTANT_DESCRIPTION'
                    )
                  }}
                </span>
              </span>

              <span class="mt-5 block">
                <span class="flex items-center justify-between gap-3 text-xs">
                  <span class="font-medium text-n-slate-11">
                    {{
                      t(
                        'CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_READY_PERCENT',
                        { value: assistantReadinessPercentage(assistant) }
                      )
                    }}
                  </span>
                  <span
                    v-if="hasUnpublishedChanges(assistant)"
                    class="font-semibold text-n-amber-11"
                  >
                    {{
                      t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_UNPUBLISHED')
                    }}
                  </span>
                </span>
                <span
                  class="mt-2 block h-1.5 overflow-hidden rounded-full bg-n-alpha-3"
                  role="progressbar"
                  :aria-label="
                    t(
                      'CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_READINESS_LABEL',
                      { name: assistant.name }
                    )
                  "
                  :aria-valuenow="assistantReadinessPercentage(assistant)"
                  :aria-valuetext="
                    t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_READY_PERCENT', {
                      value: assistantReadinessPercentage(assistant),
                    })
                  "
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <span
                    class="block h-full rounded-full bg-n-brand transition-[width]"
                    :style="{
                      width: `${assistantReadinessPercentage(assistant)}%`,
                    }"
                  />
                </span>
              </span>

              <span
                class="mt-auto flex items-center justify-between gap-3 border-t border-n-weak pt-4 text-xs text-n-slate-10"
              >
                <span v-if="formatApiDate(assistant.updated_at)">
                  {{
                    t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_UPDATED_AT', {
                      date: formatApiDate(assistant.updated_at),
                    })
                  }}
                </span>
                <span
                  class="ml-auto inline-flex items-center gap-1 font-semibold text-n-blue-11"
                >
                  {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.DIRECTORY_OPEN_AGENT') }}
                  <span
                    class="i-lucide-arrow-right size-3.5 transition group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </span>
              </span>
            </button>
          </section>
        </template>

        <section
          v-if="canManageAssistant"
          class="flex flex-col gap-4 rounded-2xl border border-n-weak bg-n-alpha-2 p-5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="i-lucide-plug-zap mt-0.5 size-5 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('CAPTAIN_EXPERIENCE.WORKSPACE.EXTERNAL_AGENTS_TITLE') }}
              </h2>
              <p class="mt-1 max-w-2xl text-sm leading-relaxed text-n-slate-11">
                {{
                  t('CAPTAIN_EXPERIENCE.WORKSPACE.EXTERNAL_AGENTS_DESCRIPTION')
                }}
              </p>
            </div>
          </div>
          <div class="flex shrink-0 flex-wrap gap-2">
            <Button
              color="slate"
              variant="faded"
              icon="i-lucide-webhook"
              :label="t('CAPTAIN_EXPERIENCE.WORKSPACE.EXTERNAL_AGENTS_ACTION')"
              @click="openExternalAdapters"
            />
            <Button
              color="slate"
              variant="ghost"
              icon="i-lucide-external-link"
              :label="
                t('CAPTAIN_EXPERIENCE.WORKSPACE.EXTERNAL_INTEGRATIONS_ACTION')
              "
              @click="openExternalIntegrations"
            />
          </div>
        </section>
      </div>
    </template>
  </PageLayout>
</template>
